import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { contentService } from '../../../../services/contentService';
import { userService } from '../../../../services/userService';
import { useApi } from '../../../../hooks/useApi';
import type { ContentDataType } from '../index';
import { 
  transformApiContentsToTableData, 
  buildApiParams, 
} from '../../utils/apiTransformers';

export interface UseContentManagementReturn {
  // Data
  tableData: ContentDataType[];
  contentCount: number;
  progressCount: number;
  publishedCount: number;
  
  // Loading states
  isLoading: boolean;
  isDeleting: boolean;
  isUpdating: boolean;
  
  // Error handling
  error: string | null;
  
  // Actions
  loadContents: (filter?: string, searchText?: string) => Promise<void>;
  deleteContent: (item: ContentDataType) => Promise<boolean>;
  updateContentStatus: (item: ContentDataType, newStatus: 'published' | 'draft') => Promise<boolean>;
  unpublishContent: (item: ContentDataType) => Promise<void>;
  republishContent: (sk: string, version: string) => Promise<boolean>;
  refreshData: () => Promise<void>;
  clearError: () => void;
  fetchContents: () => Promise<ContentDataType[] | undefined>;
  messageContext: React.ReactNode;
}

export const useContentManagement = (): UseContentManagementReturn => {
  // State
  const [tableData, setTableData] = useState<ContentDataType[]>([]);
  const [contentCount, setContentCount] = useState(0);
  const [progressCount, setProgressCount] = useState(0);
  const [publishedCount, setPublishedCount] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [currentFilter, setCurrentFilter] = useState<string>('');
  const [currentSearch, setCurrentSearch] = useState<string>('');
  const [messageApi, messageContext] = message.useMessage();

  // API hook
  const api = useApi();

  // Load contents from API
  const loadContents = useCallback(async (filter?: string, searchText?: string) => {
    try {
      const params = buildApiParams(filter, searchText);
      
      // Fetch from both endpoints simultaneously
      const [apiResponse, publicResponse] = await Promise.all([
        api.execute(() => contentService.getContents(params)), // /api/contents (with token)
        api.execute(() => contentService.getPublicContents(params)) // /contents (no token)
      ]);
      
      let combinedData: ContentDataType[] = [];
      
      if (apiResponse && apiResponse.contents) {
        const apiData = transformApiContentsToTableData(apiResponse.contents);
        for(const item of apiData) {
          item.status = item.edit_status;
        }
        combinedData = [...apiData];
      }
      
      if (publicResponse && publicResponse.contents) {
        const publicData = transformApiContentsToTableData(publicResponse.contents);
        publicData.forEach(publicItem => {
          const existingApiIndex = combinedData.findIndex(
            apiItem => apiItem.dxNumber === publicItem.dxNumber && apiItem.edit_status === 'published'
          );
          if (existingApiIndex !== -1) {
            console.log('Replacing item:', combinedData[existingApiIndex], 'with', publicItem);
            combinedData[existingApiIndex] = publicItem;
          } else {
            combinedData.push(publicItem);
          }
        });
      }
      // sort by updatedAt descending
      combinedData.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      
      // Store original creator values before showing loading state
      for(const item of combinedData) {
        if (item.content_owner) {
          // Store original creator name as backup
          (item as any).originalCreator = item.creator;
          item.creator = 'กำลังโหลด...';
        }
      }

      setCurrentFilter(filter || '');
      setCurrentSearch(searchText || '');
      setTableData(combinedData);

      (async () => {
        const emailToNameCache = new Map<string, string>();
        
        // Collect unique emails
        const uniqueEmails = new Set<string>();
        for(const item of combinedData) {
          if (item.content_owner) uniqueEmails.add(item.content_owner);
          if ((item as any).updatedByEmail) uniqueEmails.add((item as any).updatedByEmail);
        }
        
        // Fetch employee info for all unique emails
        for(const email of uniqueEmails) {
          try {
            const response = await userService.searchEmployees(email);
            if (response.employees && response.employees.length > 0) {
              const employee = response.employees[0];
              // Use Thai name if available, otherwise fall back to English name
              const fullName = employee.first_name_th && employee.last_name_th
                ? `${employee.first_name_th} ${employee.last_name_th}`
                : employee.first_name_en && employee.last_name_en
                ? `${employee.first_name_en} ${employee.last_name_en}`
                : null; // Return null if no name found, will fallback later
              
              if (fullName) {
                emailToNameCache.set(email, fullName);
              }
            }
          } catch (error) {
            console.error(`Failed to fetch employee info for ${email}:`, error);
            // Don't set anything in cache, will use fallback
          }
        }
        
        // Update the table data with fetched names
        setTableData(prevData => {
          return prevData.map(item => {
            const updatedItem = { ...item };
            
            // For creator: try to get name from cache, otherwise use original creator value
            if (item.content_owner) {
              if (emailToNameCache.has(item.content_owner)) {
                updatedItem.creator = emailToNameCache.get(item.content_owner)!;
              } else {
                // Fallback to original creator name if name not found
                updatedItem.creator = (item as any).originalCreator || item.creator;
              }
            }
            
            // For updatedBy: try to get name from cache, otherwise use email as fallback
            const originalEmail = (item as any).updatedByEmail;
            if (originalEmail) {
              if (emailToNameCache.has(originalEmail)) {
                updatedItem.updatedBy = emailToNameCache.get(originalEmail)!;
              } else {
                // Fallback to email if name not found
                updatedItem.updatedBy = originalEmail;
              }
            }
            
            return updatedItem;
          });
        });
      })();
      
    } catch (error) {
      console.error('❌ Failed to load contents:', error);
      messageApi.error('โหลดเนื้อหาไม่สำเร็จ');
    }
  }, [api]);

  const fetchContents = useCallback(async () => {
    try {
      // Fetch from both endpoints simultaneously
      const [apiResponse, publicResponse] = await Promise.all([
        api.execute(() => contentService.getContents()),
        api.execute(() => contentService.getPublicContents())
      ]);
      
      let combinedData: ContentDataType[] = [];
      
      if (apiResponse && apiResponse.contents) {
        const apiData = transformApiContentsToTableData(apiResponse.contents);
        combinedData = [...apiData];
      }
      
      if (publicResponse && publicResponse.contents) {
        const publicData = transformApiContentsToTableData(publicResponse.contents);
        
        // Merge logic: handle duplicate dx_numbers
        publicData.forEach(publicItem => {
          const existingApiIndex = combinedData.findIndex(
            apiItem => apiItem.dxNumber === publicItem.dxNumber && apiItem.status === 'published'
          );
          
          if (existingApiIndex !== -1) {
            // If same dx_number and both are published, replace with public data
            combinedData[existingApiIndex] = publicItem;
          } else {
            // If no conflict or api item is draft, add public item
            combinedData.push(publicItem);
          }
        });
      }
      
      return combinedData;
    } catch (error) {
      console.error('❌ Failed to load contents:', error);
      messageApi.error('โหลดเนื้อหาไม่สำเร็จ');
      return undefined;
    }
  }, [api]);

  // Delete content
  const deleteContent = useCallback(async (item: ContentDataType): Promise<boolean> => {
    setIsDeleting(true);
    try {
      await api.execute(() => contentService.deleteContent(item.sk));
      
      // Remove from local state
      setTableData(prevData => prevData.filter(dataItem => dataItem.sk !== item.sk));
      
      messageApi.success(`ลบรายการสำเร็จ`);
      return true;
    } catch (error) {
      console.error('❌ Failed to delete content:', error);
      messageApi.error('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
      return false;
    } finally {
      setIsDeleting(false);
    }
  }, [api]);

  // Update content status (publish/unpublish)
  const updateContentStatus = useCallback(async (
    item: ContentDataType, 
    newStatus: 'published' | 'draft'
  ): Promise<boolean> => {
    setIsUpdating(true);
    try {
      const apiStatus = newStatus === 'published' ? 'published' : 'draft';
      
      await api.execute(() => contentService.updateContent(item.sk, { 
        status: apiStatus 
      }));
      
      // Update local state
      const componentStatus = newStatus === 'published' ? 'published' : 'progress';
      setTableData(prevData =>
        prevData.map(dataItem =>
          dataItem.key === item.key 
            ? { ...dataItem, status: componentStatus }
            : dataItem
        )
      );
      
      const statusText = newStatus === 'published' ? 'published' : 'unpublished';
      messageApi.success(`Content "${item.title}" ${statusText} successfully`);
      return true;
    } catch (error) {
      console.error(`❌ Failed to update content status:`, error);
      messageApi.error('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, [api]);

  // Unpublish content
  const unpublishContent = useCallback(async (item: ContentDataType): Promise<void> => {
    setIsUpdating(true);
    try {
      await api.execute(() => contentService.unpublishContent(item.sk));
      
      // Update local state
      setTableData(prevData =>
        prevData.map(dataItem =>
          dataItem.key === item.key 
            ? { ...dataItem, status: 'progress' }
            : dataItem
        )
      );
      
      messageApi.success(`ถอนการเผยแพร่สำเร็จ`);
    } catch (error) {
      console.error('❌ Failed to unpublish content:', error);
      messageApi.error('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
    } finally {
      setIsUpdating(false);
    }
  }, [api]);

  // Republish content with specific version
  const republishContent = useCallback(async (sk: string, version: string): Promise<boolean> => {
    setIsUpdating(true);
    try {
      await api.execute(() => contentService.republishContent(sk, version));
      messageApi.success('เผยแพร่ใหม่สำเร็จ');
      return true;
    } catch (error) {
      console.error('❌ Failed to republish content:', error);
      messageApi.error('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, [api]);

  // Refresh current data
  const refreshData = useCallback(async () => {
    await loadContents(currentFilter, currentSearch);
  }, [loadContents, currentFilter, currentSearch]);

  // Clear error
  const clearError = useCallback(() => {
    api.clearError();
  }, [api]);

  // Update counts when data changes
  useEffect(() => {
    setContentCount(tableData.length);
    setProgressCount(tableData.filter(item => item.status === "draft").length);
    setPublishedCount(tableData.filter(item => item.status === "published").length);
  }, [tableData]);

  // Load initial data
  useEffect(() => {
    const timer = setTimeout(() => {
      loadContents();
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  return {
    // Data
    tableData,
    contentCount,
    progressCount,
    publishedCount,
    
    // Loading states
    isLoading: api.isLoading,
    isDeleting,
    isUpdating,
    
    // Error handling
    error: api.error,
    
    // Actions
    loadContents,
    deleteContent,
    updateContentStatus,
    refreshData,
    unpublishContent,
    republishContent,
    clearError,
    fetchContents,
    messageContext
  };
};